import { useMemo, useState } from 'react'
import { resolveCatalogProductPurchasability } from '../../purchasability'
import type { CatalogProduct, CatalogProductFieldDefinition } from '../../types'
import { buildProductCustomRows, getZoomGallery, resolveProductType } from './helpers'
import { extractLinkedProductRefs } from './linkedProducts'
import { useProductCartActions } from './model/useProductCartActions'
import {
  buildVariantProductLookup,
  getVariantKey,
  resolveProductDisplayPrices,
  resolveVariantPurchasability,
} from './model/productVariants'
import { useLinkedProducts } from './useLinkedProducts'
import { useProductCategoryBreadcrumbs } from './useProductCategoryBreadcrumbs'
import { useRecentlyViewedProducts } from './useRecentlyViewedProducts'

type Params = {
  productData: CatalogProduct | null
  productGallery: string[]
  activeProductImage: string
  productFieldDefinitions: CatalogProductFieldDefinition[]
}

export const useProductPageModel = ({
  productData,
  productGallery,
  activeProductImage,
  productFieldDefinitions,
}: Params) => {
  const [zoomOpen, setZoomOpen] = useState(false)
  const [selectedVariantKey, setSelectedVariantKey] = useState('')
  const categoryBreadcrumbs = useProductCategoryBreadcrumbs(productData)
  const recentlyViewedProducts = useRecentlyViewedProducts(productData)

  const productVariants = useMemo(() => productData?.variants ?? [], [productData?.variants])
  const selectedVariant = useMemo(
    () =>
      productVariants.find((variant, index) => getVariantKey(variant, index) === selectedVariantKey) ??
      productVariants[0] ??
      null,
    [productVariants, selectedVariantKey],
  )
  const effectiveSelectedVariantKey = useMemo(() => {
    const index = productVariants.findIndex((variant) => variant === selectedVariant)
    return selectedVariant && index >= 0 ? getVariantKey(selectedVariant, index) : ''
  }, [productVariants, selectedVariant])
  const { priceExVat, priceIncVat } = resolveProductDisplayPrices(productData, selectedVariant)
  const description = selectedVariant?.description?.trim() || productData?.description || ''
  const cartProductLookup = useMemo(() => {
    if (!productData?._id) return ''
    return selectedVariant && effectiveSelectedVariantKey
      ? buildVariantProductLookup(productData._id, effectiveSelectedVariantKey)
      : productData._id
  }, [effectiveSelectedVariantKey, productData?._id, selectedVariant])
  const metadataRows = useMemo(
    () =>
      (productData?.metadata ?? []).filter(
        (entry) => entry.key.trim().length > 0 && entry.value.trim().length > 0,
      ),
    [productData?.metadata],
  )
  const customFields = useMemo(
    () => ({
      ...(productData?.customFields ?? {}),
      ...(selectedVariant?.attributes ?? {}),
      ...(selectedVariant?.overrides ?? {}),
    }),
    [productData?.customFields, selectedVariant?.attributes, selectedVariant?.overrides],
  )
  const resolvedProductType = useMemo(
    () =>
      resolveProductType({
        productData,
        productFieldDefinitions,
      }),
    [productData, productFieldDefinitions],
  )
  const {
    summaryCustomRows,
    highlightsCustomRows,
    sidebarCustomRows,
    specsCustomRows,
    detailCustomRows,
    specsSections,
    detailSections,
  } = useMemo(
    () =>
      buildProductCustomRows({
        customFields,
        productFieldDefinitions,
        resolvedProductType,
      }),
    [customFields, productFieldDefinitions, resolvedProductType],
  )
  const linkedProductRefs = useMemo(
    () =>
      extractLinkedProductRefs({
        metadataRows,
        customRows: [...highlightsCustomRows, ...specsCustomRows, ...detailCustomRows],
      }),
    [detailCustomRows, highlightsCustomRows, metadataRows, specsCustomRows],
  )
  const zoomGallery = useMemo(
    () => getZoomGallery(activeProductImage, productGallery),
    [activeProductImage, productGallery],
  )
  const purchasability = useMemo(() => {
    const base = resolveCatalogProductPurchasability(productData)
    return resolveVariantPurchasability({ product: productData, variant: selectedVariant, base })
  }, [productData, selectedVariant])
  const {
    itemCount,
    cartBusy,
    cartQuantity,
    cartMessage,
    cartMessageTone,
    handleIncreaseCartQuantity,
    handleDecreaseCartQuantity,
    handleAddToCart,
  } = useProductCartActions({
    productData,
    productLookup: cartProductLookup,
    purchasability,
  })

  const { linkedProducts, linkedProductsLoading } = useLinkedProducts({
    productId: productData?._id ?? null,
    linkedProductRefs,
  })

  return {
    itemCount,
    cartBusy,
    zoomOpen,
    setZoomOpen,
    cartQuantity,
    cartMessage,
    cartMessageTone,
    priceExVat,
    priceIncVat,
    description,
    productVariants,
    selectedVariant,
    selectedVariantKey: effectiveSelectedVariantKey,
    setSelectedVariantKey,
    metadataRows,
    categoryBreadcrumbs,
    summaryCustomRows,
    highlightsCustomRows,
    sidebarCustomRows,
    specsCustomRows,
    detailCustomRows,
    specsSections,
    detailSections,
    linkedProducts,
    linkedProductsLoading,
    recentlyViewedProducts,
    purchasability,
    zoomGallery,
    handleIncreaseCartQuantity,
    handleDecreaseCartQuantity,
    handleAddToCart,
  }
}
